/**
 * Gift Wrap functionality for cart items
 * Adds/removes gift wrapping (20 DKK) for individual cart items
 * Adapted for Minimog theme cart events
 */

class GiftWrapManager {
  constructor() {
    this.giftWrapVariantId = null;
    this.init();
  }

  init() {
    document.addEventListener('click', this.handleCheckboxChange.bind(this));
    document.addEventListener('updateCart', this.onCartUpdate.bind(this));
  }

  getGiftWrapHandle() {
    const checkbox = document.querySelector('[data-gift-wrap-handle]');
    return checkbox ? checkbox.dataset.giftWrapHandle : 'gift-wrapping';
  }

  async getGiftWrapVariantId() {
    if (this.giftWrapVariantId) {
      return this.giftWrapVariantId;
    }

    try {
      const handle = this.getGiftWrapHandle();
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) {
        console.error('Gift wrap product not found');
        return null;
      }
      const product = await response.json();
      this.giftWrapVariantId = product.variants[0].id;
      return this.giftWrapVariantId;
    } catch (error) {
      console.error('Error fetching gift wrap product:', error);
      return null;
    }
  }

  async handleCheckboxChange(event) {
    const checkbox = event.target.closest('.gift-wrap-input');
    if (!checkbox) return;

    const container = checkbox.closest('.gift-wrap-checkbox');
    if (!container) return;

    const parentKey = container.dataset.parentKey;
    const parentVariantId = container.dataset.parentVariantId;
    const parentQuantity = parseInt(container.dataset.parentQuantity, 10);
    const parentTitle = container.dataset.parentTitle;

    if (!parentKey) return;

    container.classList.add('loading');

    try {
      const giftWrapVariantId = await this.getGiftWrapVariantId();
      if (!giftWrapVariantId) {
        throw new Error('Could not get gift wrap variant ID');
      }

      if (checkbox.checked) {
        await this.addGiftWrap(giftWrapVariantId, parentKey, parentVariantId, parentQuantity, parentTitle);
      } else {
        await this.removeGiftWrapForParent(parentKey);
      }

      // Trigger Minimog cart update events
      document.dispatchEvent(new CustomEvent('updateCart'));
      if (window.MinimogEvents && window.MinimogTheme) {
        MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, { source: 'gift-wrap' });
      }
    } catch (error) {
      console.error('Gift wrap error:', error);
      checkbox.checked = !checkbox.checked;
    } finally {
      container.classList.remove('loading');
    }
  }

  async addGiftWrap(variantId, parentKey, parentVariantId, quantity, parentTitle) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        items: [{
          id: parseInt(variantId, 10),
          quantity: quantity,
          properties: {
            '_parent_key': parentKey,
            '_parent_variant_id': parentVariantId,
            'Gaveindpakning til': parentTitle
          }
        }]
      })
    });

    if (!response.ok) {
      throw new Error('Failed to add gift wrap');
    }

    return response.json();
  }

  async removeGiftWrapForParent(parentKey) {
    const cart = await this.getCart();
    const giftWrapItem = cart.items.find(item =>
      item.properties && item.properties['_parent_key'] === parentKey
    );

    if (giftWrapItem) {
      await this.updateItemQuantity(giftWrapItem.key, 0);
    }
  }

  async syncGiftWrapQuantity(parentKey, newQuantity) {
    const cart = await this.getCart();
    const giftWrapItem = cart.items.find(item =>
      item.properties && item.properties['_parent_key'] === parentKey
    );

    if (giftWrapItem && giftWrapItem.quantity !== newQuantity) {
      if (newQuantity === 0) {
        await this.updateItemQuantity(giftWrapItem.key, 0);
      } else {
        await this.updateItemQuantity(giftWrapItem.key, newQuantity);
      }
    }
  }

  async updateItemQuantity(itemKey, quantity) {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        updates: {
          [itemKey]: quantity
        }
      })
    });

    if (!response.ok) {
      throw new Error('Failed to update cart');
    }

    return response.json();
  }

  async getCart() {
    const response = await fetch('/cart.js');
    return response.json();
  }

  onCartUpdate() {
    // Re-check gift wrap state after cart updates
  }
}

// Singleton instance
let giftWrapManagerInstance = null;

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    giftWrapManagerInstance = new GiftWrapManager();
  });
} else {
  giftWrapManagerInstance = new GiftWrapManager();
}

window.GiftWrapManager = GiftWrapManager;

window.removeGiftWrapForParent = async function(parentKey) {
  if (giftWrapManagerInstance) {
    await giftWrapManagerInstance.removeGiftWrapForParent(parentKey);
  }
};

window.syncGiftWrapQuantity = async function(parentKey, newQuantity) {
  if (giftWrapManagerInstance) {
    await giftWrapManagerInstance.syncGiftWrapQuantity(parentKey, newQuantity);
  }
};
